import { Button, Sheet } from "@mui/joy";
import {
  Avatar,
  Box,
  Divider,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  DataGridPremium as DataGridPro,
  GridActionsCellItem,
  GridCellParams,
  GridColumns,
  GridColumnVisibilityModel,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  MuiEvent,
  GridRenderCellParams,
  Toolbar,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { getProjectById } from "../../kronos-kit";
import { Dispatch } from "@reduxjs/toolkit";
import { ConfirmOptions, useConfirm } from "material-ui-confirm";
import { useContext, useEffect, useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useSearchParams } from "react-router-dom";
import {
  currencyFormatter,
  defaultDataGridProps,
  handleArrowNavigationForNumberCells,
  injectDefaultColumnProps,
} from "../../constants";
import { Job, Job_Entity } from "../../entities/job";
import { Position_Entity } from "../../entities/position";
import { Project } from "../../entities/project";
import { Talent_Entity } from "../../entities/talent";
import { AppConfig } from "../../environement";
import {
  selectedProjectID,
  setSelectedJobID,
  setSelectedVtransactionTalentID,
} from "../../redux/filmsReducer";
import {
  useCreateJobMutation,
  useDeleteJobMutation,
  useUpdateJobMutation,
} from "../../redux/job/jobEndpoints";
import { useJobs } from "../../redux/job/jobHooks";
import { usePositions } from "../../redux/position/positionHooks";
import { useProject } from "../../redux/project/projectHooks";
import { useProjectTypes } from "../../redux/projectType/projectTypeHooks";
import { useTalents } from "../../redux/talent/talentHooks";
import { ExcelContext } from "../excel";
import { useAppSelector } from "../hooks";
import {
  getterDivider,
  setterDivider,
} from "../jobCategory/jobCategoryRates/dataGridColDef";
import NoRowsOverlay from "../noRowsOverlay/noRowsOverlay";
import { usePendingEdits } from "../pendingEdits/pendingEdits";
import { useSaveError } from "../saveError/saveError";
import { Help } from "../projectBudget/projectBudget";
import {
  SelectTalentInputCell,
  SelectTalents,
} from "../selectTalents/selectTalents";
import TalentAvatar from "../talent/talentsList/talentAvatar";

/**
 * Job
 *
 * @returns {null} Job
 */
export default function Jobs({
  orchestrator = false,
}: {
  orchestrator?: boolean;
}) {
  const [params] = useSearchParams();
  const highlightedTalentID = params.get("talentID");
  const projectID = useSelector(selectedProjectID);
  const { project } = useProject(projectID);
  const { jobs: _jobs, isLoading: l1 } = useJobs(
    {
      filters: JSON.stringify([
        {
          name: "jobs.projectID",
          value: projectID,
          comparison: "eq",
        },
      ]),
    },
    { skip: !projectID }
  );
  const apiRef = useGridApiRef();
  const confirm = useConfirm();
  const { talents, talentsMap } = useTalents();
  const jobs = useMemo(() => {
    const filteredJobs = [...(_jobs ?? [])].filter((job) =>
      orchestrator ? job.orchestrator : !job.orchestrator
    );

    return filteredJobs.sort((a, b) => {
      const aTalent = talentsMap?.[a.talentID];
      const bTalent = talentsMap?.[b.talentID];

      const lastNameSort = (aTalent?.lastName ?? "").localeCompare(
        bTalent?.lastName ?? "",
        { sensitivity: "base" }
      );
      if (lastNameSort !== 0) return lastNameSort;

      const firstNameSort = (aTalent?.firstName ?? "").localeCompare(
        bTalent?.firstName ?? "",
        { sensitivity: "base" }
      );
      if (firstNameSort !== 0) return firstNameSort;

      return a.id - b.id;
    });
  }, [_jobs, orchestrator, talentsMap]);
  const sortedTalents = useMemo(() => {
    return [...(talents ?? [])].sort((a, b) => {
      const lastNameSort = (a.lastName ?? "").localeCompare(b.lastName ?? "", {
        sensitivity: "base",
      });
      if (lastNameSort !== 0) return lastNameSort;

      const firstNameSort = (a.firstName ?? "").localeCompare(
        b.firstName ?? "",
        { sensitivity: "base" }
      );
      if (firstNameSort !== 0) return firstNameSort;

      return a.id - b.id;
    });
  }, [talents]);
  const { positions } = usePositions();
  const { projectTypesMap } = useProjectTypes();
  const [updateJob] = useUpdateJobMutation();
  const [deleteJob] = useDeleteJobMutation();
  const dispatch = useDispatch();
  const { reportSaveError, saveErrorSnackbar } = useSaveError();
  const {
    pendingEdits,
    pendingValue,
    markPending,
    dropPending,
    releaseSettled,
  } = usePendingEdits();

  // Fresh server data: let go of the cells it has caught up with.
  useEffect(() => {
    releaseSettled((id, field) => {
      const job = _jobs?.find((j) => j.id === Number(id));
      return job?.[field];
    });
  }, [_jobs, releaseSettled]);

  // Overlay the values committed but not yet echoed back, so a later refetch
  // can't revert them.
  const rowsWithPendingEdits = useMemo(
    () =>
      jobs.map((job) => {
        const held = Object.keys(pendingEdits).filter((key) =>
          key.startsWith(`${job.id}:`)
        );
        if (!held.length) return job;
        const row = { ...job } as Job_Entity;
        held.forEach((key) => {
          const field = key.slice(key.indexOf(":") + 1);
          row[field] = pendingValue(job.id, field, row[field]);
        });
        return row;
      }),
    [jobs, pendingEdits, pendingValue]
  );

  const minimumCopyist =
    projectTypesMap![project?.projectTypeID ?? 0]?.minimumCopyist;

  const excel = useContext(ExcelContext);

  const onJobUpdate = async (_newJob: Job_Entity, _oldJob: Job_Entity) => {
    const body = { ..._newJob };

    const editedFields = [];

    for (const key in _newJob) {
      if (Object.prototype.hasOwnProperty.call(_newJob, key)) {
        if (_newJob[key] !== _oldJob[key]) {
          editedFields.push(key);
        }
      }
    }

    if (editedFields.indexOf("totalAdjusted") >= 0) {
      if (_newJob.totalAdjusted === 0) {
        body.totalAdjusted = -1;
      }
    } else {
      body.totalAdjusted = 0;
    }

    // Hold the edited fields until the server reports them, so a later refetch
    // can't revert them. Not awaited: holding the cell in edit mode for a
    // round-trip swallows whatever is typed next.
    editedFields.forEach((field) =>
      markPending(_newJob.id, field, body[field])
    );

    updateJob({ id: _newJob.id, body })
      .unwrap()
      .catch((error) => {
        editedFields.forEach((field) => dropPending(_newJob.id, field));
        reportSaveError(error);
      });

    return { ..._newJob };
  };

  const usedTalentID = new Map<number, boolean>();
  jobs.forEach((j) => {
    usedTalentID[j.talentID] = true;
  });

  let columnVisibility: GridColumnVisibilityModel = {
    hourlyRate: !orchestrator,
    subTotalNoLib: !orchestrator,
    kronosHours: !orchestrator,
    hours: !orchestrator,
    adjustment: !orchestrator,
    stipend: !orchestrator,
    effectiveHourly: !orchestrator,
    librarian: !orchestrator,
    supervisionPercent: !orchestrator,
    supervision: !orchestrator,
    stipendDescription: !orchestrator,
    totalAdjusted: !orchestrator,
    librarianSplit: !orchestrator,
    benefitsOnly: orchestrator,
  };

  if (!project?.union) {
    columnVisibility = {
      ...columnVisibility,
      flexPlanHours: false,
      flexPlan: false,
      vacation: false,
      pension: false,
      benefitsOnly: false,
    };
  }

  const copyCellValue = async (params: GridCellParams) => {
    const valueToCopy = params.value ?? params.formattedValue;
    if (valueToCopy === undefined || valueToCopy === null) return;

    const text = `${valueToCopy}`;
    if (!text.length) return;

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        return;
      } catch (_error) {
        // Fallback below.
      }
    }

    const tempInput = document.createElement("textarea");
    tempInput.value = text;
    document.body.appendChild(tempInput);
    tempInput.select();
    document.execCommand("copy");
    document.body.removeChild(tempInput);
  };

  const handleCellKeyDown = async (
    params: GridCellParams,
    event: MuiEvent<any>
  ) => {
    handleArrowNavigationForNumberCells(params, event);

    const key = event.key.toLowerCase();
    const isDeleteKey = key === "delete" || key === "backspace";
    const target = event.target as HTMLElement | null;
    const isInputTarget =
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement;
    const isSpinbutton = target?.getAttribute("role") === "spinbutton";
    const isTextInputTarget = isInputTarget || isSpinbutton;
    const isNumberField = params.colDef.type === "number";

    if (
      key === "enter" &&
      params.isEditable &&
      isNumberField &&
      isTextInputTarget &&
      isInputTarget &&
      target.value.trim() === ""
    ) {
      event.preventDefault();
      event.stopPropagation();
      await apiRef.current.setEditCellValue({
        id: params.id,
        field: params.field,
        value: 0,
      });
      apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
      return;
    }

    if (isDeleteKey && params.isEditable && !isTextInputTarget) {
      event.preventDefault();
      event.stopPropagation();
      apiRef.current.startCellEditMode({ id: params.id, field: params.field });
      await apiRef.current.setEditCellValue({
        id: params.id,
        field: params.field,
        value: isNumberField ? 0 : "",
      });
      apiRef.current.stopCellEditMode({ id: params.id, field: params.field });
      return;
    }

    const isShortcut = event.metaKey || event.ctrlKey;
    if (!isShortcut || event.altKey) return;

    // Cmd/Ctrl+V is deliberately not handled: the grid's own clipboard import
    // pastes whole ranges — one spreadsheet column into many rows — through
    // each column's pastedValueParser and processRowUpdate. Grabbing the
    // keystroke here is what used to collapse a pasted block into one cell.
    if (key === "c") {
      // Same for copy when a range is selected: the grid serializes the whole
      // selection. Only the single-cell case wants the raw value instead of
      // the formatted one, so it survives a round-trip back into the grid.
      const selectedCells = apiRef.current.getSelectedCellsAsArray();
      if (selectedCells.length > 1) return;
      event.preventDefault();
      event.stopPropagation();
      await copyCellValue(params);
      return;
    }
  };

  return (
    <Paper
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      <DataGridPro
        {...defaultDataGridProps}
        onCellKeyDown={handleCellKeyDown}
        disableColumnReorder={false}
        getCellClassName={(c) => {
          let className = "";
          const row = c.row;
          const talentID = row.talentID;
          const talent = talentsMap![talentID];
          const isPartner = talent?.positionID === 1;
          if (c.field === "totalAdjusted" && isPartner) {
            return "cellReadOnly";
          }
          className = !c.isEditable ? " cellReadOnly" : "";
          if (c.field === " librarianSplit" && !c.row.librarian) {
            className += " cellReadOnly";
          }
          if (
            !orchestrator &&
            (c.field === "total" || c.field === "subTotal") &&
            c.row.subTotal < minimumCopyist
          )
            className += " warning";

          if ((c.row.lock || project?.locked()) && c.field !== "actions")
            className += " lock";

          if (talentID === parseInt(highlightedTalentID ?? ""))
            className += " highlightedRow";

          return className;
        }}
        isCellEditable={(params) => {
          const row = params.row;
          const talentID = row.talentID;
          const talent = talentsMap![talentID];
          const isPartner = talent?.positionID === 1;
          if (isPartner && params.field === "totalAdjusted") return false;
          return params.row.lock ? false : params.colDef.editable ?? false;
        }}
        apiRef={apiRef}
        processRowUpdate={onJobUpdate}
        onProcessRowUpdateError={reportSaveError}
        rows={rowsWithPendingEdits}
        columns={jobColumns(
          excel,
          dispatch,
          project!,
          confirm,
          sortedTalents,
          talentsMap ?? {},
          positions ?? [],
          orchestrator,
          deleteJob,
          updateJob
        )}
        getDetailPanelHeight={() => 100}
        showToolbar
        slots={{
          toolbar: JobToolbar,
          noRowsOverlay: NoRowsOverlay,
          footer: JobsFooter,
        }}
        slotProps={{
          footer: { rows: jobs, orchestrator },
          toolbar: { usedTalentID, orchestrator },
          cell: { tabIndex: 1 },
        }}
        initialState={{
          columns: {
            columnVisibilityModel: columnVisibility,
          },
          pinnedColumns: {
            left: ["position", "talentID"],
            right: ["total", "totalAdjusted", "actions"],
          },
        }}
      />
      {saveErrorSnackbar}
    </Paper>
  );
}

function JobToolbar({
  usedTalentID,
  orchestrator,
}: {
  usedTalentID: Map<number, boolean>;
  orchestrator: boolean;
}) {
  const projectID = useSelector(selectedProjectID);
  const { project } = useProject(projectID);
  const { jobs: _jobs, isLoading: l1 } = useJobs(
    {
      filters: JSON.stringify([
        {
          name: "jobs.projectID",
          value: projectID,
          comparison: "eq",
        },
      ]),
    },
    { skip: !projectID }
  );
  const [anchorEl, setAnchorEl] = useState<
    (EventTarget & HTMLButtonElement) | null
  >();
  const [open, setOpen] = useState(false);
  const { talents } = useTalents();
  const sortedTalents = useMemo(() => {
    return [...(talents ?? [])].sort((a, b) => {
      const lastNameSort = (a.lastName ?? "").localeCompare(b.lastName ?? "", {
        sensitivity: "base",
      });
      if (lastNameSort !== 0) return lastNameSort;

      const firstNameSort = (a.firstName ?? "").localeCompare(
        b.firstName ?? "",
        { sensitivity: "base" }
      );
      if (firstNameSort !== 0) return firstNameSort;

      return a.id - b.id;
    });
  }, [talents]);
  const [createJob] = useCreateJobMutation();
  const dispatch = useDispatch();
  const excel = useContext(ExcelContext);

  const handleNewJob = (talentID: number) => {
    if (projectID && !project?.locked()) {
      setOpen(false);
      const newJob = {
        projectID: projectID,
        id: -1,
        talentID,
        hours: 0,
        orchestrator,
        librarian: false,
        subTotal: 0,
        supervisionPercent: 0,
        supervision: 0,
        vacation: 0,
        benefitsOnly: false,
        total: 0,
        daysWorked: 0,
        pension: 0,
        flexPlanHours: 0,
        flexPlan: 0,
        supplies: 0,
        adjustment: 0,
        createdAt: "",
        updatedAt: "",
        hourlyRate: 0,
        stipend: 0,
        stipendDescription: "",
      };
      createJob(newJob);
    }
  };

  useHotkeys(`ctrl+enter`, () => setOpen(true), {
    enableOnTags: ["INPUT", "SELECT", "TEXTAREA"],
  });

  return (
    <Toolbar>
      <Box
        sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
          <Tooltip title="ctrl + enter">
            <Button
              size="sm"
              variant="soft"
              onClick={() => setOpen(true)}
              disabled={project?.locked()}
            >
              + New Job
            </Button>
          </Tooltip>
          <SelectTalents
            open={open}
            onChange={(e) => {
              if (e.length) handleNewJob(e[0]);
            }}
            talents={sortedTalents.filter((t) => usedTalentID[t.id] != true)}
            single
            onClose={() => setOpen(false)}
          />
          <Divider
            sx={{ margin: 1, width: 2 }}
            orientation="vertical"
            variant="middle"
            flexItem
          />
          <QuickFilter
            expanded
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            <QuickFilterControl
              size="small"
              fullWidth
              placeholder="Search..."
            />
            <QuickFilterClear />
          </QuickFilter>
        </Box>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem
            onClick={() => {
              excel.invoice(_jobs ?? []);
              setAnchorEl(null);
            }}
          >
            <i className="fa-solid fa-file-excel pright"></i>Excel
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              window.open(
                `${AppConfig.api.endpoint}/films/projects/${projectID}/invoices`,
                "_blank"
              );
            }}
          >
            <i className="fa-solid fa-file-pdf pright"></i> PDF
          </MenuItem>
        </Menu>
      </Box>
    </Toolbar>
  );
}

function JobsFooter({
  rows,
  orchestrator,
}: {
  rows: Job_Entity[];
  orchestrator: boolean;
}) {
  let total = 0;
  const params = useParams();
  const projectID = parseInt(params.id ?? "");
  const { project } = useProject(projectID);
  const dispatch = useDispatch();

  for (const key in rows) {
    if (Object.prototype.hasOwnProperty.call(rows, key)) {
      const row = rows[key];
      total += row.total;
    }
  }
  return (
    <>
      <Divider />
      <Box
        sx={{
          p: 2,
          justifyContent: "space-between",
          gap: 2,
          display: "flex",
        }}
      >
        <Box>
          {!orchestrator ? (
            <Button
              onClick={() => dispatch(getProjectById(projectID))}
              variant="outlined"
              disabled={project?.locked()}
              size="small"
            >
              Calculate All Jobs
            </Button>
          ) : (
            []
          )}
        </Box>
        <Box />
        <Box>
          {!orchestrator ? (
            <>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "end" }}>
                <Typography sx={{ fontWeight: 600 }} variant="body2">
                  <Help title="No benefits, No vacation, No lib" /> Subtotal (no
                  lib)
                </Typography>
                <Typography sx={{ fontWeight: 600 }} variant="body2">
                  {project?.laborSubTotalNoLibDollar()}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 1, justifyContent: "end" }}>
                <Typography sx={{ fontWeight: 600 }} variant="body2">
                  <Help title="With Lib, Vacation, and Supervision" /> Total
                  (w/ben):
                </Typography>
                <Typography sx={{ fontWeight: 600 }} variant="body2">
                  {project?.laborTotalDollar()}
                </Typography>
              </Box>
            </>
          ) : (
            <Box sx={{ display: "flex", gap: 1, justifyContent: "end" }}>
              <Typography sx={{ fontWeight: 600 }} variant="body2">
                Subtotal (no ben):
              </Typography>
              <Typography sx={{ fontWeight: 600 }} variant="body2">
                {currencyFormatter.format(total / 10000)}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
}

const resolveGridValue = (valueOrParams: any) =>
  valueOrParams?.value ?? valueOrParams;

// Sum of the line items, each rounded to the cent first. Amounts are stored as
// 4-decimal integers, and each column is rounded independently for display, so
// `round(a) + round(b)` can differ from `round(a + b)` by a penny. Deriving the
// displayed Total this way keeps it equal to the Subtotal + Vacation the user
// actually sees on screen. Inputs are 4-decimal integers; result is in dollars.
const centsSumDollars = (...amounts: number[]): number =>
  amounts.reduce(
    (acc, amount) => acc + Math.round((amount ?? 0) / 100) / 100,
    0
  );

/**
 * Reads a clipboard number the way people paste them: with the dollar signs,
 * separators and "/h" suffixes the grid itself puts there. Anything that
 * still isn't a number returns undefined, which makes the paste skip the
 * cell instead of silently writing garbage.
 *
 * @param {string} value One clipboard cell.
 * @returns {number | undefined} The parsed number, or undefined to skip.
 */
const parseNumberFromClipboard = (value: string) => {
  const normalized = `${value ?? ""}`
    .replace(/,/g, "")
    .replace(/\$/g, "")
    .replace(/%/g, "")
    .replace(/\/h/g, "")
    .trim();
  if (!normalized.length) return undefined;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
};

/**
 * The grid's clipboard import runs a pasted cell through the column's
 * pastedValueParser — without one, "$55.00" lands in the valueSetter as NaN
 * and the cell silently keeps its old value.
 *
 * @param {Array} columns The grid's column definitions.
 * @returns {Array} The same columns, numeric editable ones made pasteable.
 */
const withNumberPasteParsing = (columns: any[]) =>
  columns.map((column) =>
    column.type === "number" && column.editable && !column.pastedValueParser
      ? { ...column, pastedValueParser: parseNumberFromClipboard }
      : column
  );

const jobColumns = (
  excel: {
    invoice: (j: Job_Entity[]) => void;
  },
  dispatch: Dispatch,
  project: Project,
  confirm: (options?: ConfirmOptions | undefined) => Promise<void>,
  talents: Talent_Entity[],
  talentsMap: { [key: number]: Talent_Entity },
  positions: Position_Entity[],
  orchestrator: boolean,
  deleteJob: (number: number) => void,
  updateJob: (data: { id: number; body: Partial<Job_Entity> }) => void
): GridColumns =>
  injectDefaultColumnProps(
    withNumberPasteParsing([
      {
        field: "position",
        headerName: "Position",
        headerAlign: "center",
        type: "string",
        sortable: false,
        width: 100,
        renderCell: (p) => {
          const talent = talents.find((t) => t.id === p.row.talentID);
          const position = positions.find((p) => p.id === talent?.positionID);
          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                width: "100%",
                overflow: "hidden",
              }}
            >
              {p.row.lock ? (
                <i
                  className="fa-solid fa-lock"
                  style={{ marginRight: 4, flexShrink: 0 }}
                ></i>
              ) : (
                []
              )}
              <Typography
                variant="body2"
                noWrap
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {position?.name ?? ""}
              </Typography>
            </Box>
          );
        },
        valueGetter: (_value, row) => {
          const talent = talents.find((t) => t.id === row.talentID);
          const position = positions.find((p) => p.id === talent?.positionID);
          return position?.id ?? "";
        },
      },
      {
        field: "talentID",
        headerAlign: "center",
        headerName: "Talent",
        sortable: false,
        editable: true,
        width: 170,
        pastedValueParser: (value: string) => {
          const text = value?.trim();
          if (!text) return undefined;

          const numericID = Number(text);
          if (Number.isFinite(numericID)) {
            const talentByID = talents.find((t) => t.id === numericID);
            if (talentByID) return talentByID.id;
          }

          const normalized = text.toLowerCase();
          const talentByName = talents.find((t) => {
            const fullName = `${t.firstName} ${t.lastName}`.toLowerCase();
            const reversedName = `${t.lastName}, ${t.firstName}`.toLowerCase();
            const code = `${t.code ?? ""}`.toLowerCase();
            return (
              fullName === normalized ||
              reversedName === normalized ||
              code === normalized
            );
          });

          return talentByName?.id;
        },
        renderEditCell: (p) => {
          return (
            <SelectTalentInputCell
              id={p.id}
              single
              field={p.field}
              value={[p.row.talentID]}
              talents={talents}
            />
          );
        },
        renderCell: (p) => {
          const talent = talents.find((t) => t.id === p.row.talentID);
          return (
            <Box
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "center",
                minWidth: 0,
                width: "100%",
                overflow: "hidden",
              }}
            >
              <TalentAvatar
                talent={talent}
                size={28}
                active={talent?.active ?? false}
              />
              <Typography
                variant="body2"
                noWrap
                sx={{
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                color={talent ? "textPrimary" : "textSecondary"}
              >
                {talent
                  ? `${talent?.firstName} ${talent?.lastName}`
                  : "Double Click to assign"}
              </Typography>
            </Box>
          );
        },
      },
      // {
      //   field: "orchestrator",
      //   headerAlign: "center",
      //   headerName: "Orch",
      //   sortable: false,
      //   editable: true,
      //   width: 60,
      //   type: "boolean",
      // },
      {
        field: "librarian",
        headerAlign: "center",
        headerName: "Lib",
        sortable: false,
        editable: true,
        width: 60,
        type: "boolean",
      },
      {
        field: "benefitsOnly",
        headerAlign: "center",
        headerName: "Ben Only",
        sortable: false,
        editable: true,
        width: 80,
        type: "boolean",
      },
      {
        field: "hourlyRate",
        headerAlign: "center",
        headerName: "Hourly Rate",
        sortable: false,
        editable: true,
        type: "number",
        valueGetter: getterDivider("hourlyRate", 10000),
        valueSetter: setterDivider("hourlyRate", 10000),
        valueFormatter: (valueOrParams: any) =>
          `${currencyFormatter.format(resolveGridValue(valueOrParams))}/h`,
      },
      {
        field: "hours",
        headerAlign: "center",
        align: "right",
        headerName: "Hours",
        sortable: false,
        editable: true,
        type: "number",
        width: 70,
        valueGetter: getterDivider("hours", 100),
        valueSetter: setterDivider("hours", 100),
      },
      {
        field: "kronosHours",
        headerAlign: "center",
        align: "right",
        headerName: "Hours Tracked",
        width: 120,
        sortable: false,
        editable: false,
        type: "number",
        valueGetter: getterDivider("kronosHours", 100),
        valueSetter: setterDivider("kronosHours", 100),
        renderCell: (p) => <CopyOver job={p.row} />,
      },
      {
        field: "effectiveHourly",
        headerAlign: "center",
        headerName: "Hourly",
        sortable: false,
        editable: false,
        width: 90,
        type: "number",
        valueGetter: (_value: any, row: any) =>
          row.hours > 0 ? row.total / row.hours : 0,
        valueFormatter: (valueOrParams: any) =>
          `${currencyFormatter.format(
            resolveGridValue(valueOrParams) / 100
          )}/h`,
      },
      {
        field: "adjustment",
        headerAlign: "center",
        headerName: "Adjustment",
        sortable: false,
        editable: true,
        type: "number",
        valueGetter: getterDivider("adjustment", 10000),
        valueSetter: setterDivider("adjustment", 10000),
        valueFormatter: (valueOrParams: any) =>
          `${currencyFormatter.format(resolveGridValue(valueOrParams))}`,
      },
      {
        field: "subTotalNoLib",
        headerAlign: "center",
        align: "right",
        headerName: "Subtotal (no lib)",
        editable: false,
        sortable: false,
        width: 125,
        type: "number",
        valueGetter: getterDivider("subTotalNoLib", 10000),
        valueSetter: setterDivider("subTotalNoLib", 10000),
        valueFormatter: (valueOrParams: any) =>
          `${currencyFormatter.format(resolveGridValue(valueOrParams))}`,
      },
      {
        field: "librarianSplit",
        headerAlign: "center",
        headerName: "Librarian",
        width: 80,
        sortable: false,
        editable: true,
        type: "number",
        valueGetter: getterDivider("librarianSplit", 10000),
        valueSetter: setterDivider("librarianSplit", 10000),
        valueFormatter: (valueOrParams: any) =>
          `${currencyFormatter.format(resolveGridValue(valueOrParams))}`,
      },
      {
        field: "supervisionPercent",
        headerAlign: "center",
        align: "center",
        headerName: "Supervision %",
        editable: false,
        sortable: false,
        type: "number",
        width: 110,
        valueGetter: getterDivider("supervisionPercent", 100),
        valueSetter: setterDivider("supervisionPercent", 100),
        valueFormatter: (valueOrParams: any) =>
          `${resolveGridValue(valueOrParams)}%`,
      },
      {
        field: "supervision",
        headerAlign: "center",
        headerName: "Supervision",
        editable: false,
        sortable: false,
        type: "number",
        valueGetter: getterDivider("supervision", 10000),
        valueSetter: setterDivider("supervision", 10000),
        valueFormatter: (valueOrParams: any) =>
          `${currencyFormatter.format(resolveGridValue(valueOrParams))}`,
      },
      {
        field: "subTotal",
        headerAlign: "center",
        align: "right",
        headerName: "Subtotal",
        editable: false,
        sortable: false,
        type: "number",
        valueGetter: getterDivider("subTotal", 10000),
        valueSetter: setterDivider("subTotal", 10000),
        valueFormatter: (valueOrParams: any) =>
          `${currencyFormatter.format(resolveGridValue(valueOrParams))}`,
        renderCell: !orchestrator
          ? (p) => <RenderCopysitTotal {...p} />
          : undefined,
      },
      {
        field: "vacation",
        headerAlign: "center",
        headerName: "Vacation",
        editable: false,
        sortable: false,
        width: 80,
        type: "number",
        valueGetter: getterDivider("vacation", 10000),
        valueSetter: setterDivider("vacation", 10000),
        valueFormatter: (valueOrParams: any) =>
          `${currencyFormatter.format(resolveGridValue(valueOrParams))}`,
      },
      {
        field: "payrollPercent",
        headerAlign: "center",
        align: "center",
        headerName: "Payroll %",
        editable: false,
        sortable: false,
        type: "number",
        width: 90,
        valueGetter: getterDivider("payrollPercent", 100),
        valueSetter: setterDivider("payrollPercent", 100),
        valueFormatter: (valueOrParams: any) =>
          `${resolveGridValue(valueOrParams)}%`,
      },
      {
        field: "payroll",
        headerAlign: "center",
        headerName: "Payroll",
        editable: false,
        sortable: false,
        width: 90,
        type: "number",
        valueGetter: getterDivider("payroll", 10000),
        valueSetter: setterDivider("payroll", 10000),
        valueFormatter: (valueOrParams: any) =>
          `${currencyFormatter.format(resolveGridValue(valueOrParams))}`,
      },
      // {
      //   field: "daysWorked",
      //   align: "center",
      //   headerAlign: "center",
      //   headerName: "Days Wkd",
      //   sortable: false,
      //   editable: false,
      //   width: 90,
      //   type: "number",
      //   valueGetter: getterDivider("daysWorked", 1),
      //   valueSetter: setterDivider("daysWorked", 1),
      // },
      // {
      //   field: "flexPlanHours",
      //   align: "center",
      //   headerAlign: "center",
      //   headerName: "Flex Plan Hrs",
      //   editable: false,
      //   sortable: false,
      //   type: "number",
      //   width: 105,
      //   valueGetter: getterDivider("flexPlanHours", 100),
      //   valueSetter: setterDivider("flexPlanHours", 100),
      // },
      // {
      //   field: "flexPlan",
      //   headerAlign: "center",
      //   headerName: "Flex Plan",
      //   editable: false,
      //   sortable: false,
      //   width: 80,
      //   type: "number",
      //   valueGetter: getterDivider("flexPlan", 10000),
      //   valueSetter: setterDivider("flexPlan", 10000),
      //   valueFormatter: (valueOrParams: any) =>
      //     `${currencyFormatter.format(resolveGridValue(valueOrParams))}`,
      // },
      // {
      //   field: "pension",
      //   headerAlign: "center",
      //   headerName: "Pension",
      //   editable: false,
      //   sortable: false,
      //   width: 80,
      //   type: "number",
      //   valueGetter: getterDivider("pension", 10000),
      //   valueSetter: setterDivider("pension", 10000),
      //   valueFormatter: (valueOrParams: any) =>
      //     `${currencyFormatter.format(resolveGridValue(valueOrParams))}`,
      // },
      {
        field: "stipend",
        headerAlign: "center",
        headerName: "Stipend",
        width: 80,
        sortable: false,
        editable: true,
        type: "number",
        valueGetter: getterDivider("stipend", 10000),
        valueSetter: setterDivider("stipend", 10000),
        valueFormatter: (valueOrParams: any) =>
          `${currencyFormatter.format(resolveGridValue(valueOrParams))}`,
      },
      {
        field: "stipendDescription",
        headerAlign: "center",
        headerName: "Stipend Desc",
        editable: true,
        sortable: false,
        width: 150,
        type: "string",
      },
      {
        field: "total",
        align: "right",
        headerAlign: "center",
        headerName: orchestrator ? "Total" : "Total (no adj)",
        editable: false,
        sortable: false,
        width: orchestrator ? 100 : 120,
        type: "number",
        // Orchestrator Total = Subtotal + Vacation. Derive it from the cent-rounded
        // columns so it matches the Subtotal and Vacation the user sees, instead of
        // rounding the raw sum (which could be a penny off). Copyist totals are
        // rendered by RenderCopysitTotal and keep the raw stored value.
        valueGetter: orchestrator
          ? (_value: any, row: any) =>
              centsSumDollars(row.subTotal, row.vacation)
          : getterDivider("total", 10000),
        valueSetter: setterDivider("total", 10000),
        valueFormatter: (valueOrParams: any) =>
          `${currencyFormatter.format(resolveGridValue(valueOrParams))}`,
        renderCell: !orchestrator
          ? (p) => <RenderCopysitTotal {...p} />
          : undefined,
      },
      {
        field: "totalAdjusted",
        align: "right",
        headerAlign: "center",
        headerName: "Total Adjusted",
        editable: true,
        sortable: false,
        width: 120,
        type: "number",
        valueGetter: (_value: any, row: any) => row.total / 10000,
        valueSetter: setterDivider("totalAdjusted", 10000),
        renderCell: (c) => {
          const talentID = c.row.talentID;
          const talent = talentsMap[talentID];
          const isPartner = talent?.positionID === 1;

          return (
            <Box
              sx={{
                display: "flex",
                justifyContent: isPartner ? "space-between" : "end",
                flex: 1,
                alignItems: "center",
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              {isPartner ? (
                <Tooltip
                  title={`Cannot calculate adjustment for partners. Must be manually adjusted.`}
                >
                  <i className="fa-regular fa-circle-question"></i>
                </Tooltip>
              ) : (
                []
              )}
              <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
                {currencyFormatter.format(c.value)}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: "actions",
        type: "actions",
        sortable: false,
        headerName: "",
        width: 30,
        cellClassName: "actions",
        getActions: (props: { row: Job_Entity; id: any }) => {
          const actions = [
            <GridActionsCellItem
              key="delete"
              disabled={project.locked()}
              icon={<i className="fa-solid fa-trash"></i>}
              label={`Delete`}
              onClick={() => {
                confirm({
                  description: `You are about to delete a job`,
                })
                  .then(() => {
                    deleteJob(props.id);
                  })
                  .catch(() => {
                    /* ... */
                  });
              }}
              color="inherit"
              showInMenu
            />,
          ];

          if (props.row.talentID) {
            actions.push(
              <GridActionsCellItem
                key={"vtransactions"}
                icon={<i className="fa-solid fa-file-pdf"></i>}
                label="Download PDF"
                onClick={() => {
                  window.open(
                    `${AppConfig.api.endpoint}/films/jobs/${props.row.id}/invoice`,
                    "_blank"
                  );
                }}
                showInMenu
              />
            );
          }

          if (props.row.talentID) {
            actions.push(
              <GridActionsCellItem
                key={"vtransactions"}
                icon={<i className="fa-solid fa-scale-unbalanced"></i>}
                label="Ledger transactions"
                onClick={() => {
                  dispatch(setSelectedVtransactionTalentID(props.row.talentID));
                  dispatch(setSelectedJobID(props.id));
                }}
                showInMenu
              />
            );
          }

          if (props.row.talentID) {
            actions.push(
              <GridActionsCellItem
                key={props.row.lock ? "Unlock" : "lock"}
                disabled={project.locked()}
                icon={
                  <i
                    className={
                      props.row.lock
                        ? "fa-regular fa-lock-open"
                        : "fa-solid fa-lock"
                    }
                  ></i>
                }
                sx={{
                  background: !props.row.lock
                    ? "rgba(76, 175, 80, 0.1)"
                    : undefined,
                }}
                label={props.row.lock ? "Unlock" : "Lock"}
                onClick={() => {
                  updateJob({
                    id: props.row.id,
                    body: { lock: !props.row.lock },
                  });
                }}
                showInMenu
              />
            );
          }

          return actions;
        },
      },
    ])
  );

function RenderCopysitTotal(p: GridRenderCellParams) {
  const { projectTypesMap } = useProjectTypes();
  const projectID = useSelector(selectedProjectID);
  const { project } = useProject(projectID);

  if (!project) return <Box>{currencyFormatter.format(p.value as number)}</Box>;

  const minimumCopyist = projectTypesMap
    ? projectTypesMap[project.projectTypeID]?.minimumCopyist
    : 0;

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 2,
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      {p.row.subTotal < minimumCopyist ? (
        <Tooltip
          title={`Min pay is ${currencyFormatter.format(
            minimumCopyist / 10000
          )}`}
        >
          <i className="fa-regular fa-triangle-exclamation"></i>
        </Tooltip>
      ) : (
        <Box />
      )}
      <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
        {p.field === "total"
          ? currencyFormatter.format((p.row.total - p.row.adjustment) / 10000)
          : currencyFormatter.format(p.value as number)}
      </Typography>
    </Box>
  );
}

function CopyOver({ job }: { job: Job }) {
  const [updateJob] = useUpdateJobMutation();

  const handleCopyOver = () => {
    updateJob({ id: job.id, body: { ...job, hours: job.kronosHours } });
  };

  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        justifyContent: "space-between",
        flex: 1,
        minWidth: 0,
        overflow: "hidden",
        cursor: "pointer",
      }}
    >
      {job.kronosHours && job.hours != job.kronosHours ? (
        <Tooltip title="Copy over">
          <Sheet
            onClick={handleCopyOver}
            color="primary"
            variant="solid"
            sx={{
              height: 20,
              width: 20,
              justifyContent: "center",
              alignItems: "center",
              display: "flex",
              borderRadius: 4,
            }}
          >
            <i className="fa-solid fa-arrow-left"></i>
          </Sheet>
        </Tooltip>
      ) : (
        <div />
      )}
      {job.kronosHours ? (
        <Typography variant="body2" noWrap sx={{ minWidth: 0 }}>
          <i className="fa-solid fa-timer" style={{ paddingRight: 4 }}></i>
          {job.kronosHours / 100}
        </Typography>
      ) : (
        <Typography
          variant="caption"
          noWrap
          sx={{ opacity: 0.8, minWidth: 0, textOverflow: "ellipsis" }}
        >
          <i>No time tracked</i>
        </Typography>
      )}
    </Box>
  );
}
